import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import React, { memo } from 'react';
import { useController } from 'react-hook-form';

import RangeSlider from '../../RangeSlider/RangeSlider';

// Clamp the min handle: must stay between the slider's min and the current max value.
// Mirrors PriceFilterForm's parseMin so the clamp behavior is identical.
const clampMin = (value, min, currentMax) => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return min;
  }
  return parsedValue < min ? min : parsedValue > currentMax ? currentMax : parsedValue;
};

// Clamp the max handle: must stay between the current min value and the slider's max.
// Mirrors PriceFilterForm's parseMax so the clamp behavior is identical.
const clampMax = (value, max, currentMin) => {
  const parsedValue = Number.parseInt(value, 10);
  if (isNaN(parsedValue)) {
    return max;
  }
  return parsedValue < currentMin ? currentMin : parsedValue > max ? max : parsedValue;
};

/**
 * ControlledRangeSlider is a dual-handle numeric range slider that reads and writes TWO
 * react-hook-form fields (a min field and a max field) from a single component. It wraps
 * the existing RangeSlider UI and clamps both handles identically to PriceFilterForm's
 * parseMin/parseMax.
 *
 * Because it controls two fields at once, it uses two `useController` instances driven by
 * the shared `control` object plus explicit field names (`name` for the min field and
 * `maxName` for the max field).
 *
 * @param {object} props
 * @param {string} props.name - The name of the min (lower handle) field. Required.
 * @param {string} props.maxName - The name of the max (upper handle) field. Required.
 * @param {import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form. Required.
 * @param {string} [props.label] - The label for the slider
 * @param {boolean} [props.required] - The required state of the field
 * @param {boolean} [props.error] - The error state of the field
 * @param {string | React.ReactNode | any} [props.helperText] - The helper text for the field
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name' | 'control'>} [props.controllerOptions] - The options for both controllers
 * @param {number} [props.min] - The minimum selectable value of the slider
 * @param {number} [props.max] - The maximum selectable value of the slider
 * @param {number} [props.step] - The step between selectable values
 * @param {object} [props.rangeSliderProps] - Additional props forwarded to the RangeSlider component
 */

const ControlledRangeSliderComponent = ({
  name,
  maxName,
  control,
  label = '',
  required = false,
  error = false,
  helperText = '',
  controllerOptions = {},
  min = 0,
  max = 10000000,
  step = 1,
  rangeSliderProps = {},
}) => {
  if (!name) {
    throw new Error('ControlledRangeSlider: name is required');
  }
  if (!maxName) {
    throw new Error('ControlledRangeSlider: maxName is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledRangeSlider: control is required');
  }

  const id = `id_${name}`;

  const { field: minField } = useController({
    name,
    control,
    ...controllerOptions,
  });
  const { field: maxField } = useController({
    name: maxName,
    control,
    ...controllerOptions,
  });

  const minValue = typeof minField.value === 'number' ? minField.value : min;
  const maxValue = typeof maxField.value === 'number' ? maxField.value : max;

  const handleChange = (handles) => {
    const nextMin = clampMin(handles[0], min, maxValue);
    const nextMax = clampMax(handles[1], max, minValue);
    minField.onChange(nextMin);
    maxField.onChange(nextMax);
  };

  return (
    <Stack spacing={1} width="100%">
      {label ? (
        <InputLabel htmlFor={id} required={required} shrink={false}>
          {label}
        </InputLabel>
      ) : null}
      <RangeSlider
        handles={[minValue, maxValue]}
        max={max}
        min={min}
        onChange={handleChange}
        step={step}
        {...rangeSliderProps}
      />
      {error ? <FormHelperText error>{helperText}</FormHelperText> : null}
    </Stack>
  );
};

ControlledRangeSliderComponent.displayName = 'ControlledRangeSlider';

const ControlledRangeSlider = memo(ControlledRangeSliderComponent);

export default ControlledRangeSlider;
